import React, { useEffect, useRef, useState } from "react";
import { Search, User, UserPlus, Tag as TagIcon, X } from "lucide-react";
import { api, formatErr } from "../lib/api";

/**
 * CustomerPicker — picker unificato per scegliere un cliente (user registrato o contact business).
 *
 * Props:
 *   businessId: required
 *   value: { kind: 'user'|'contact'|'walkin', id, name, email, phone, contactId } | null
 *   onChange: (selection) => void
 *   allowWalkin: boolean (default true) — mostra opzione "Walk-in (solo nome)"
 *   allowCreate: boolean (default true) — mostra opzione "Nuovo contatto"
 *   placeholder: string
 *
 * UX:
 *  - Input ricerca debounced 250ms
 *  - Dropdown con risultati (max 20) con icona + tag
 *  - Sotto: "+ Aggiungi nuovo contatto" che apre form inline (nome obbligatorio, email/phone facoltativi)
 *  - Sotto: "Solo nome (walk-in)" se allowWalkin
 *  - Quando selezionato: card riepilogo + bottone "Cambia"
 */
export default function CustomerPicker({
  businessId,
  value,
  onChange,
  allowWalkin = true,
  allowCreate = true,
  placeholder = "Cerca cliente per nome, email o telefono…",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [walkinMode, setWalkinMode] = useState(false);
  const [walkinName, setWalkinName] = useState("");
  const [newForm, setNewForm] = useState({ name: "", email: "", phone: "" });
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/businesses/${businessId}/contacts/search`, { params: { q } });
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, businessId]);

  function pick(r) {
    // For "past" customers (guest/walkin from previous bookings), auto-save as contact
    // so they become reusable across Manual Booking and Regulars.
    if (r.kind === "past") {
      pickPast(r);
      return;
    }
    onChange({
      kind: r.kind,
      id: r.id,
      name: r.name,
      email: r.email,
      phone: r.phone,
      contactId: r.contact_id || null,
    });
    setQ("");
    setResults([]);
  }

  async function pickPast(r) {
    // If we already have email/phone, persist as a business contact for future reuse
    setErr("");
    if (!r.email && !r.phone) {
      // No contact data captured → fallback to walk-in with the name only
      onChange({ kind: "walkin", id: null, name: r.name, email: null, phone: null, contactId: null });
      setQ("");
      setResults([]);
      return;
    }
    try {
      const payload = { name: r.name };
      if (r.email) payload.email = r.email;
      if (r.phone) payload.phone = r.phone;
      const { data } = await api.post(`/businesses/${businessId}/contacts`, payload);
      onChange({
        kind: "contact",
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        contactId: data.id,
      });
    } catch (e) {
      // 409 = email already taken → search the existing contact instead
      if (e.response?.status === 409 && r.email) {
        try {
          const { data } = await api.get(`/businesses/${businessId}/contacts`, { params: { q: r.email } });
          const found = (data || []).find((c) => (c.email || "").toLowerCase() === r.email.toLowerCase());
          if (found) {
            onChange({
              kind: "contact",
              id: found.id,
              name: found.name,
              email: found.email,
              phone: found.phone,
              contactId: found.id,
            });
            setQ("");
            setResults([]);
            return;
          }
        } catch {
          // ignore
        }
      }
      // Last resort: fallback to walk-in preserving the name
      onChange({ kind: "walkin", id: null, name: r.name, email: r.email, phone: r.phone, contactId: null });
    }
    setQ("");
    setResults([]);
  }

  async function createContact(e) {
    e?.preventDefault();
    setErr("");
    if (!newForm.name || newForm.name.length < 2) {
      setErr("Inserisci almeno nome e cognome");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: newForm.name.trim(),
      };
      if (newForm.email) payload.email = newForm.email.trim().toLowerCase();
      if (newForm.phone) payload.phone = newForm.phone.trim();
      const { data } = await api.post(`/businesses/${businessId}/contacts`, payload);
      onChange({
        kind: "contact",
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        contactId: data.id,
      });
      setNewForm({ name: "", email: "", phone: "" });
      setQ("");
      setResults([]);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore creazione contatto");
    } finally {
      setCreating(false);
    }
  }

  function pickWalkin() {
    if (!walkinName || walkinName.length < 2) {
      setErr("Inserisci almeno il nome");
      return;
    }
    onChange({
      kind: "walkin",
      id: null,
      name: walkinName.trim(),
      email: null,
      phone: null,
      contactId: null,
    });
    setWalkinMode(false);
    setWalkinName("");
    setErr("");
  }

  // SELECTED state
  if (value) {
    return (
      <div className="rounded-xl border border-zinc-900 bg-zinc-900 text-white p-3 flex items-start gap-3" data-testid="customer-picker-selected">
        <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
          {value.kind === "walkin" ? <UserPlus className="h-5 w-5" /> : <User className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{value.name}</div>
          <div className="text-xs text-white/60 truncate flex items-center gap-2 mt-0.5">
            {value.email && <span>{value.email}</span>}
            {value.phone && <span>· {value.phone}</span>}
            {value.kind === "walkin" && <span className="px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-100 text-[10px] uppercase tracking-wider">Walk-in</span>}
            {value.kind === "user" && <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/30 text-emerald-100 text-[10px] uppercase tracking-wider">Account</span>}
            {value.kind === "contact" && <span className="px-1.5 py-0.5 rounded-full bg-blue-500/30 text-blue-100 text-[10px] uppercase tracking-wider">Contatto</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-white/70 hover:text-white px-3 py-1 rounded-full hover:bg-white/10"
          data-testid="customer-picker-change"
        >
          Cambia
        </button>
      </div>
    );
  }

  // SEARCH state
  return (
    <div className="space-y-2" data-testid="customer-picker">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); } }}
          placeholder={placeholder}
          className="input-soft pl-10"
          autoFocus
          data-testid="customer-picker-search"
        />
        {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 dark:text-zinc-500">…</div>}
      </div>

      {results.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white max-h-72 overflow-y-auto dark:border-zinc-700 dark:bg-zinc-900" data-testid="customer-picker-results">
          {results.map((r) => (
            <button
              key={`${r.kind}-${r.id}`}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 flex items-center gap-3 dark:hover:bg-zinc-800 dark:border-zinc-800"
              data-testid={`customer-picker-result-${r.kind}-${r.id}`}
            >
              <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0 dark:bg-zinc-800">
                <User className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{r.name}</div>
                <div className="text-xs text-zinc-500 truncate dark:text-zinc-400">
                  {r.email}
                  {r.phone && r.email && " · "}
                  {r.phone}
                </div>
                {r.tags && r.tags.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {r.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 inline-flex items-center gap-0.5 dark:bg-blue-950/40 dark:text-blue-300">
                        <TagIcon className="h-2.5 w-2.5" />{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${r.kind === "user" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : r.kind === "past" ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"}`}>
                {r.kind === "user" ? "Account" : r.kind === "past" ? `Storico${r.past_count ? ` ×${r.past_count}` : ""}` : "Contatto"}
              </span>
            </button>
          ))}
        </div>
      )}

      {q && q.length >= 2 && results.length === 0 && !loading && (
        <div className="text-xs text-zinc-500 py-2 px-3 dark:text-zinc-400">Nessun cliente trovato.</div>
      )}

      {/* Quick-create new contact (no nested <form> — bubbles into parent booking form) */}
      {allowCreate && (
        <details className="rounded-xl border border-zinc-200 bg-white overflow-hidden dark:border-zinc-700 dark:bg-zinc-900" data-testid="customer-picker-new-details">
          <summary className="px-4 py-3 cursor-pointer hover:bg-zinc-50 flex items-center gap-2 text-sm dark:hover:bg-zinc-800" data-testid="customer-picker-new-summary">
            <UserPlus className="h-4 w-4 text-blue-600" />
            <span className="font-medium">Aggiungi nuovo contatto</span>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">· salva nome, email e telefono</span>
          </summary>
          <div className="p-4 space-y-2 border-t border-zinc-100 dark:border-zinc-800" data-testid="customer-picker-new-form">
            <input
              type="text"
              required
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); createContact(); } }}
              placeholder="Nome e cognome *"
              className="input-soft text-sm"
              data-testid="customer-picker-new-name"
            />
            <input
              type="email"
              value={newForm.email}
              onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); createContact(); } }}
              placeholder="Email (facoltativa)"
              className="input-soft text-sm"
              data-testid="customer-picker-new-email"
            />
            <input
              type="tel"
              value={newForm.phone}
              onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); createContact(); } }}
              placeholder="Telefono (facoltativo)"
              className="input-soft text-sm"
              data-testid="customer-picker-new-phone"
            />
            {err && <div className="text-xs text-red-600">{err}</div>}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); createContact(); }}
              disabled={creating}
              className="btn-accent w-full text-sm disabled:opacity-50"
              data-testid="customer-picker-new-submit"
            >
              {creating ? "Salvataggio…" : "Crea contatto"}
            </button>
          </div>
        </details>
      )}

      {/* Walk-in fallback */}
      {allowWalkin && (
        <details className="rounded-xl border border-zinc-200 bg-white overflow-hidden dark:border-zinc-700 dark:bg-zinc-900" data-testid="customer-picker-walkin-details">
          <summary
            className="px-4 py-3 cursor-pointer hover:bg-zinc-50 flex items-center gap-2 text-sm dark:hover:bg-zinc-800"
            onClick={() => setWalkinMode(true)}
            data-testid="customer-picker-walkin-summary"
          >
            <UserPlus className="h-4 w-4 text-amber-600" />
            <span className="font-medium">Walk-in (solo nome)</span>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">· nessun dato salvato</span>
          </summary>
          <div className="p-4 space-y-2 border-t border-zinc-100 dark:border-zinc-800">
            <input
              type="text"
              value={walkinName}
              onChange={(e) => setWalkinName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); pickWalkin(); } }}
              placeholder="Nome cliente"
              className="input-soft text-sm"
              data-testid="customer-picker-walkin-name"
            />
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); pickWalkin(); }}
              disabled={!walkinName || walkinName.length < 2}
              className="btn-accent w-full text-sm disabled:opacity-50"
              data-testid="customer-picker-walkin-submit"
            >
              Usa walk-in
            </button>
          </div>
        </details>
      )}
    </div>
  );
}
